/**
 * Utilidad para generar el PDF de informe de precalificación
 * Reutilizable desde PerformanceDetailPrecalification.vue y ShowPerformance.vue
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Genera el PDF de informe de precalificación
 * @param {Object} options - Opciones para la generación del PDF
 * @param {Object} options.performance - Datos del desempeño (id, year, person, etc.)
 * @param {Array} options.activityPerformances - Lista de actividades con calificaciones
 * @param {Object} options.signatures - Estado de firmas {director, par_interno, par_externo}
 * @param {Array} options.areaWeights - Ponderaciones por área
 * @param {Object} options.weightedScoreBreakdown - Desglose de puntaje ponderado
 * @param {Array} options.availableAreas - Lista de áreas académicas disponibles
 * @param {String} options.unitName - Nombre de la unidad/departamento
 * @param {Object} options.academicInfo - Información adicional del académico (jornada, etc.)
 * @param {Function} options.formatSignatureDate - Función para formatear fechas de firma
 * @returns {Promise<{doc: jsPDF, filename: string, allSignaturesComplete: boolean}>}
 */
export async function generatePrecalificationReportPdf(options) {
  const {
    performance,
    activityPerformances,
    signatures,
    areaWeights,
    weightedScoreBreakdown,
    availableAreas,
    unitName,
    academicInfo,
    formatSignatureDate
  } = options;

  let width = 216; // 8.5 pulgadas en mm
  let height = 356; // 14 pulgadas en mm
  let doc = new jsPDF({
    orientation: "landscape",
    format: [width, height],
  });
  let pageWidth = doc.internal.pageSize.width;

  // HEADER
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  let header = [
    "UNIVERSIDAD DE SANTIAGO DE CHILE",
    "FACULTAD DE INGENIERÍA",
    unitName && unitName !== 'N/A' ? unitName.toUpperCase() : "DEPARTAMENTO",
  ];
  doc.text(header, 10, 15);

  // TITLE
  let blockTitlePosition = 25;
  doc.setFontSize(12);

  let title = "INFORME DE PRECALIFICACIÓN DE DESEMPEÑO";
  let titleWidth = doc.getTextWidth(title);
  let titleX = (pageWidth - titleWidth) / 2;
  doc.text(title, titleX, blockTitlePosition);

  blockTitlePosition += 6;
  title = `AÑO ${performance.year}`;
  titleWidth = doc.getTextWidth(title);
  titleX = (pageWidth - titleWidth) / 2;
  doc.text(title, titleX, blockTitlePosition);

  // Configuration
  let tableMargin = 12;
  let tableWidth = pageWidth - tableMargin * 2;
  let marginLeft = (pageWidth - tableWidth) / 2;

  // ACADEMIC INFORMATION
  let blockAcademicInformationPosition = blockTitlePosition + 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("I. Antecedentes Personales", 11, blockAcademicInformationPosition);

  // Período y Jornada
  blockAcademicInformationPosition += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Período: Año ${performance.year}`, marginLeft, blockAcademicInformationPosition);
  doc.text(`Jornada: ${academicInfo?.working_day_name || 'No definida'}`, marginLeft + 100, blockAcademicInformationPosition);

  // Obtener nombre de la persona
  const person = performance.person || {};
  let columns = [
    person.last_name || '',
    person.second_surname || '',
    person.name || '',
  ];
  let rows = [["Primer Apellido", "Segundo Apellido", "Nombres"]];

  blockAcademicInformationPosition += 5;
  doc.autoTable({
    head: [columns],
    body: rows,
    startY: blockAcademicInformationPosition,
    theme: "plain",
    margin: { left: marginLeft },
    columnStyles: {
      0: { cellWidth: tableWidth * 0.33 },
      1: { cellWidth: tableWidth * 0.33 },
      2: { cellWidth: tableWidth * 0.33 },
    },
    styles: {
      fontSize: 8,
      textColor: [0, 0, 0],
      fillColor: [255, 255, 255],
      halign: "center",
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontSize: 8,
      fontStyle: "bold",
      halign: "center",
    },
  });

  // DETAILED ACTIVITIES - UNA SOLA TABLA (en la misma página)
  let currentY = doc.lastAutoTable.finalY + 15;
  
  // Si no hay espacio suficiente, hacer salto de página
  if (currentY > 280) {
    doc.addPage();
    currentY = 15;
  }
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("II. Detalle de Actividades y Calificaciones", 11, currentY);
  
  currentY += 5;

  // Función para normalizar nombres de áreas (remover acentos, guiones, mayúsculas)
  const normalizeAreaName = (name) => {
    if (!name) return '';
    return name.toUpperCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remover acentos
      .replace(/-/g, ' ') // Reemplazar guiones por espacios
      .replace(/\s+/g, ' ') // Normalizar espacios múltiples
      .trim();
  };

  // Agrupar actividades por nombre de área normalizado (usando alt_name_area)
  let activitiesByAreaName = {};
  activityPerformances.forEach(act => {
    const areaName = normalizeAreaName(act.alt_name_area || '');
    if (areaName) {
      if (!activitiesByAreaName[areaName]) {
        activitiesByAreaName[areaName] = [];
      }
      activitiesByAreaName[areaName].push(act);
    }
  });

  // Construir todas las filas para una sola tabla
  let allRows = [];
  
  // Función para buscar área en availableAreas con normalización
  const findAreaByName = (searchName) => {
    const normalizedSearch = normalizeAreaName(searchName);
    return availableAreas.find(a => normalizeAreaName(a.name) === normalizedSearch);
  };
  
  // Función para obtener actividades de un área por nombre
  const getActivitiesByAreaName = (searchName) => {
    const normalizedSearch = normalizeAreaName(searchName);
    return activitiesByAreaName[normalizedSearch] || null;
  };
  
  // Usar todas las áreas académicas disponibles
  const allAcademicAreas = [
    'Docencia',
    'Investigación y Desarrollo',
    'Asistencia Técnica',
    'Perfeccionamiento',
    'Administración Académica',
    'Extensión-VIME',
    'Educación Continua-VIME'
  ];
  
  for (const areaName of allAcademicAreas) {
    // Buscar actividades por nombre de área
    let areaActivities = getActivitiesByAreaName(areaName);
    
    const normalizedAreaName = normalizeAreaName(areaName);
    
    // Obtener el peso del área desde weightedScoreBreakdown si está disponible
    let weightPercentage = 0;
    let areaBreakdown = null;
    
    if (weightedScoreBreakdown && weightedScoreBreakdown.breakdown) {
      areaBreakdown = weightedScoreBreakdown.breakdown.find(b => 
        normalizeAreaName(b.area_name) === normalizedAreaName
      );
      if (areaBreakdown) {
        weightPercentage = parseFloat(areaBreakdown.weight_percentage);
      }
    }
    
    // Si no está en breakdown, buscar en areaWeights
    if (weightPercentage === 0 && areaWeights) {
      let areaWeight = areaWeights.find(w => 
        normalizeAreaName(w.area_name) === normalizedAreaName
      );
      weightPercentage = areaWeight ? parseFloat(areaWeight.weight_percentage) : 0;
    }
    
    // Calcular totales del área
    let totalCommittedSem1 = 0;
    let totalCompletedSem1 = 0;
    let totalCommittedSem2 = 0;
    let totalCompletedSem2 = 0;
    let areaAverageScore = 0;
    let activitiesWithScore = 0;
    
    if (areaActivities && areaActivities.length > 0) {
      areaActivities.forEach(act => {
        totalCommittedSem1 += parseFloat(act.hours_committed_semester_1) || 0;
        totalCompletedSem1 += parseFloat(act.hours_completed_semester_1) || 0;
        totalCommittedSem2 += parseFloat(act.hours_committed_semester_2) || 0;
        totalCompletedSem2 += parseFloat(act.hours_completed_semester_2) || 0;
        
        if (act.precalification_score) {
          areaAverageScore += parseFloat(act.precalification_score);
          activitiesWithScore++;
        }
      });
      
      if (activitiesWithScore > 0) {
        areaAverageScore = areaAverageScore / activitiesWithScore;
      }
    }
    
    // Si tenemos breakdown, usar su area_average
    if (areaBreakdown && parseFloat(areaBreakdown.area_average) > 0) {
      areaAverageScore = parseFloat(areaBreakdown.area_average);
    }
    
    let totalCommittedAnnual = totalCommittedSem1 + totalCommittedSem2;
    let totalCompletedAnnual = totalCompletedSem1 + totalCompletedSem2;
    let partialScore = '';
    
    // Calcular puntaje parcial usando breakdown si está disponible
    if (areaBreakdown && parseFloat(areaBreakdown.weighted_contribution) > 0) {
      partialScore = parseFloat(areaBreakdown.weighted_contribution).toFixed(2);
    } else if (areaAverageScore > 0 && weightPercentage > 0) {
      partialScore = (areaAverageScore * weightPercentage / 100).toFixed(2);
    }
    
    // Agregar fila de encabezado del área con totales
    allRows.push([
      { content: areaName.toUpperCase(), styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'left' } },
      { content: totalCommittedSem1 > 0 ? totalCommittedSem1.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: totalCompletedSem1 > 0 ? totalCompletedSem1.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: totalCommittedSem2 > 0 ? totalCommittedSem2.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: totalCompletedSem2 > 0 ? totalCompletedSem2.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: totalCommittedAnnual > 0 ? totalCommittedAnnual.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: totalCompletedAnnual > 0 ? totalCompletedAnnual.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: areaAverageScore > 0 ? areaAverageScore.toFixed(2) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: weightPercentage > 0 ? weightPercentage.toFixed(1) + '%' : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
      { content: '', styles: { fillColor: [220, 220, 220] } },
      { content: partialScore, styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } }
    ]);
    
    // Si hay actividades, agregarlas
    if (areaActivities && areaActivities.length > 0) {
      areaActivities.forEach(act => {
        let hoursCommittedSem1 = parseFloat(act.hours_committed_semester_1) || 0;
        let hoursCompletedSem1 = parseFloat(act.hours_completed_semester_1) || 0;
        let hoursCommittedSem2 = parseFloat(act.hours_committed_semester_2) || 0;
        let hoursCompletedSem2 = parseFloat(act.hours_completed_semester_2) || 0;
        let hoursCommittedAnnual = hoursCommittedSem1 + hoursCommittedSem2;
        let hoursCompletedAnnual = hoursCompletedSem1 + hoursCompletedSem2;
        
        // Obtener nombre de actividad
        let activityName = act.alt_name_activity || (act.academic_activity ? act.academic_activity.name : 'Actividad');
        
        allRows.push([
          activityName,
          hoursCommittedSem1.toFixed(1),
          hoursCompletedSem1.toFixed(1),
          hoursCommittedSem2.toFixed(1),
          hoursCompletedSem2.toFixed(1),
          hoursCommittedAnnual.toFixed(1),
          hoursCompletedAnnual.toFixed(1),
          act.precalification_score || '',
          '', // Ponderación vacía para actividades individuales
          act.precalification_comment || '',
          '' // Puntaje parcial vacío para actividades individuales
        ]);
      });
    } else {
      // Si no hay actividades, agregar fila vacía
      allRows.push(['', '', '', '', '', '', '', '', '', '', '']);
    }
  }
  
  // Calcular totales generales para la fila TOTAL
  let grandTotalCommittedSem1 = 0;
  let grandTotalCompletedSem1 = 0;
  let grandTotalCommittedSem2 = 0;
  let grandTotalCompletedSem2 = 0;
  let grandTotalCommittedAnnual = 0;
  let grandTotalCompletedAnnual = 0;
  
  // Sumar todos los totales de las áreas usando activitiesByAreaName
  for (const areaName of allAcademicAreas) {
    let areaActivities = getActivitiesByAreaName(areaName);
    
    if (areaActivities && areaActivities.length > 0) {
      areaActivities.forEach(act => {
        grandTotalCommittedSem1 += parseFloat(act.hours_committed_semester_1) || 0;
        grandTotalCompletedSem1 += parseFloat(act.hours_completed_semester_1) || 0;
        grandTotalCommittedSem2 += parseFloat(act.hours_committed_semester_2) || 0;
        grandTotalCompletedSem2 += parseFloat(act.hours_completed_semester_2) || 0;
      });
    }
  }
  
  grandTotalCommittedAnnual = grandTotalCommittedSem1 + grandTotalCommittedSem2;
  grandTotalCompletedAnnual = grandTotalCompletedSem1 + grandTotalCompletedSem2;
  
  // Calcular suma total de ponderaciones (debería dar 1.00)
  let totalWeightPercentage = 0;
  if (weightedScoreBreakdown && weightedScoreBreakdown.breakdown) {
    weightedScoreBreakdown.breakdown.forEach(b => {
      totalWeightPercentage += parseFloat(b.weight_percentage);
    });
  }
  let totalWeightDisplay = totalWeightPercentage > 0 ? (totalWeightPercentage / 100).toFixed(2) : '';
  
  // Agregar fila TOTAL
  let totalScore = weightedScoreBreakdown ? parseFloat(weightedScoreBreakdown.total_score).toFixed(2) : '';
  
  // Calcular categoría de la nota final
  const getFinalScoreCategory = (score) => {
    if (!score) return null;
    const scoreNum = parseFloat(score);
    if (scoreNum >= 3.70 && scoreNum <= 4.00) {
      return { label: 'Sobresaliente', type: 'is-success' };
    } else if (scoreNum >= 2.80 && scoreNum < 3.70) {
      return { label: 'Bueno', type: 'is-info' };
    } else if (scoreNum >= 2.00 && scoreNum < 2.80) {
      return { label: 'Aceptable', type: 'is-warning' };
    } else if (scoreNum >= 1.50 && scoreNum < 2.00) {
      return { label: 'Condicional', type: 'is-warning' };
    } else if (scoreNum >= 0.00 && scoreNum < 1.50) {
      return { label: 'Insuficiente', type: 'is-danger' };
    }
    return null;
  };
  
  const finalScoreCategory = getFinalScoreCategory(totalScore);
  
  allRows.push([
    { content: 'TOTAL', styles: { fontStyle: 'bold', halign: 'left' } },
    { content: grandTotalCommittedSem1.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: grandTotalCompletedSem1.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: grandTotalCommittedSem2.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: grandTotalCompletedSem2.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: grandTotalCommittedAnnual.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: grandTotalCompletedAnnual.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: totalScore, styles: { fontStyle: 'bold', halign: 'center' } }, // Calif.
    { content: totalWeightDisplay, styles: { fontStyle: 'bold', halign: 'center' } }, // Pond.
    '', // Observaciones vacía
    { content: totalScore, styles: { fontStyle: 'bold', halign: 'center' } } // Puntaje Parcial
  ]);
  
  // Agregar fila sin etiqueta con totales/2 y estado
  allRows.push([
    '', // Actividad vacía
    '', '', '', '', // Semestres vacíos
    { content: (grandTotalCommittedAnnual / 2).toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: (grandTotalCompletedAnnual / 2).toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
    { content: finalScoreCategory ? finalScoreCategory.label.toUpperCase() : '', colSpan: 2, styles: { fontStyle: 'bold', halign: 'center' } }, // Calif. + Pond. (estado)
    '', // Observaciones vacía
    '' // Puntaje Parcial vacío
  ]);

  // Crear una sola tabla con todas las actividades
  doc.autoTable({
    head: [[
      "Actividad",
      { content: "1° Semestre", colSpan: 2 },
      { content: "2° Semestre", colSpan: 2 },
      { content: "Anual", colSpan: 2 },
      "Calif.",
      "Pond.",
      "Observaciones e Información adicional",
      "Puntaje Parcial"
    ], [
      "",
      "Comprometidas",
      "Realizadas",
      "Comprometidas",
      "Realizadas",
      "Comprometidas",
      "Realizadas",
      "",
      "",
      "",
      ""
    ]],
    body: allRows,
    startY: currentY,
    theme: "grid",
    margin: { left: marginLeft },
    columnStyles: {
      0: { cellWidth: tableWidth * 0.18 },  // Actividad
      1: { cellWidth: tableWidth * 0.07 },  // 1°S Comprometidas
      2: { cellWidth: tableWidth * 0.07 },  // 1°S Realizadas
      3: { cellWidth: tableWidth * 0.07 },  // 2°S Comprometidas
      4: { cellWidth: tableWidth * 0.07 },  // 2°S Realizadas
      5: { cellWidth: tableWidth * 0.07 },  // Anual Comprometidas
      6: { cellWidth: tableWidth * 0.07 },  // Anual Realizadas
      7: { cellWidth: tableWidth * 0.05 },  // Calificación
      8: { cellWidth: tableWidth * 0.05 },  // Ponderación
      9: { cellWidth: tableWidth * 0.20 },  // Observaciones
      10: { cellWidth: tableWidth * 0.10 }, // Puntaje Parcial
    },
    styles: { 
      fontSize: 6.5, 
      textColor: [0, 0, 0], 
      halign: "center",
      valign: "middle",
      cellPadding: 1.5
    },
    headStyles: { 
      fillColor: [255, 255, 255], 
      textColor: [0, 0, 0], 
      lineColor: 200, 
      lineWidth: 0.25,
      fontSize: 6.5,
      fontStyle: "bold",
      halign: "center"
    },
    bodyStyles: {
      0: { halign: "left" },   // Actividad alineada a izquierda
      9: { halign: "left" },   // Observaciones alineada a izquierda
    },
    didParseCell: function(data) {
      // Ajustar altura de fila para las observaciones si son largas
      if (data.column.index === 9 && data.cell.raw && data.cell.raw.length > 50) {
        data.cell.styles.cellPadding = 2;
      }
    }
  });

  // Verificar si están las 3 firmas completas
  const allSignaturesComplete = signatures.director.signed && 
                                 signatures.par_interno.signed && 
                                 signatures.par_externo.signed;

  // Si están las 3 firmas, mostrar sección de firmas
  if (allSignaturesComplete) {
    // SIGNATURES - En nueva página
    doc.addPage();
    let blockSignaturesPosition = 30;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("III. Firmas de la Comisión Precalificadora", 11, blockSignaturesPosition);
    blockSignaturesPosition += 40;
    
    // Layout for 3 signatures
    let sigWidth = tableWidth / 3;
    
    // Director
    let x1 = marginLeft + sigWidth * 0.5;
    doc.line(x1 - 30, blockSignaturesPosition, x1 + 30, blockSignaturesPosition);
    doc.setFontSize(8);
    doc.text("Director(a) de Departamento", x1, blockSignaturesPosition + 5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("FIRMADO DIGITALMENTE", x1, blockSignaturesPosition - 15, { align: "center" });
    doc.text(signatures.director.name, x1, blockSignaturesPosition - 10, { align: "center" });
    if (performance.director_signature_date && formatSignatureDate) {
      doc.text(formatSignatureDate(performance.director_signature_date), x1, blockSignaturesPosition - 5, { align: "center" });
    }

    // Par Interno
    let x2 = marginLeft + sigWidth * 1.5;
    doc.setFont("helvetica", "bold");
    doc.line(x2 - 30, blockSignaturesPosition, x2 + 30, blockSignaturesPosition);
    doc.setFontSize(8);
    doc.text("Comisión Miembro 1", x2, blockSignaturesPosition + 5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("FIRMADO DIGITALMENTE", x2, blockSignaturesPosition - 15, { align: "center" });
    doc.text(signatures.par_interno.name, x2, blockSignaturesPosition - 10, { align: "center" });
    if (performance.committee_member_1_sign_date && formatSignatureDate) {
      doc.text(formatSignatureDate(performance.committee_member_1_sign_date), x2, blockSignaturesPosition - 5, { align: "center" });
    }

    // Par Externo
    let x3 = marginLeft + sigWidth * 2.5;
    doc.setFont("helvetica", "bold");
    doc.line(x3 - 30, blockSignaturesPosition, x3 + 30, blockSignaturesPosition);
    doc.setFontSize(8);
    doc.text("Comisión Miembro 2", x3, blockSignaturesPosition + 5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("FIRMADO DIGITALMENTE", x3, blockSignaturesPosition - 15, { align: "center" });
    doc.text(signatures.par_externo.name, x3, blockSignaturesPosition - 10, { align: "center" });
    if (performance.committee_member_2_sign_date && formatSignatureDate) {
      doc.text(formatSignatureDate(performance.committee_member_2_sign_date), x3, blockSignaturesPosition - 5, { align: "center" });
    }
  }

  // LOGO
  let imgPath = "/logo_negro_facultad_ing.png";
  let imgWidth = 60;
  let imgX = pageWidth - imgWidth - 10;
  let imgY = 5;

  try {
    const response = await fetch(imgPath);
    const blob = await response.blob();
    const base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    doc.setPage(1);
    doc.addImage(base64Image, "PNG", imgX, imgY, imgWidth, 0);
  } catch (e) {
    console.error("Error loading logo", e);
  }

  // Si NO están las 3 firmas, agregar marca de agua "BORRADOR" en todas las páginas
  if (!allSignaturesComplete) {
    const totalPages = doc.internal.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.height;
    
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Guardar estado gráfico
      doc.saveGraphicsState();
      
      // Aplicar transparencia usando GState
      doc.setGState(new doc.GState({ opacity: 0.12 }));
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(45);
      doc.setTextColor(180, 180, 180);
      
      // Crear patrón de "BORRADOR" en toda la página
      const rows = 5;
      const cols = 2;
      const stepY = pageHeight / rows;
      const stepX = pageWidth / cols;
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const x = (col + 0.5) * stepX;
          const y = (row + 0.5) * stepY;
          
          doc.text("BORRADOR", x, y, {
            align: "center",
            angle: 45
          });
        }
      }
      
      // Restaurar estado gráfico (incluyendo opacidad)
      doc.restoreGraphicsState();
      
      // Restaurar color de texto para contenido normal
      doc.setTextColor(0, 0, 0);
    }
  }

  // Obtener RUT de la persona
  const personRut = person.rut || performance.person_id || 'UNKNOWN';
  const filename = `INFORME_PRECALIFICACION_${personRut}_${performance.year}${!allSignaturesComplete ? '_BORRADOR' : ''}.pdf`;

  return {
    doc,
    filename,
    allSignaturesComplete
  };
}

/**
 * Descarga directamente el PDF generado
 * @param {Object} options - Mismas opciones que generatePrecalificationReportPdf
 */
export async function downloadPrecalificationReportPdf(options) {
  const result = await generatePrecalificationReportPdf(options);
  result.doc.save(result.filename);
  return result;
}
